import type { FC } from 'react'
import { CuesGroupTimingsWidget } from './cues-group-timings.widget.tsx'
import { cn } from '@/utils'
import { GroupRange, type GroupTiming } from '@/entities/cues-group'
import { DimmedDuration } from '@/shared/ui/dimmed-duration'
import { isBefore, isAfter } from 'date-fns'
import { roundMsToSeconds } from '@rundown-studio/timeutils'

type CuesGroupActualTimingProps = {
  groupTiming: GroupTiming | null
  timezone?: string | null
  todDisplayFormat?: '24h' | '12hNoAmPm' | '12h' | null
}

/**
 * Shows actual timing for groups that have completed or differ from original timing.
 * Displays duration and time range with red/green colors indicating performance.
 */
export const CuesGroupActualTiming: FC<CuesGroupActualTimingProps> = ({
  groupTiming,
  timezone,
  todDisplayFormat,
}) => {
  if (!groupTiming) return null

  // Check if actual duration exceeds original duration (using round to match visual time display)
  const isOverdue =
    roundMsToSeconds(groupTiming.actual.duration, Math.round) >
    roundMsToSeconds(groupTiming.original.duration, Math.round)

  return (
    <CuesGroupTimingsWidget
      className="bg-gray-900 py-0.5"
      Duration={
        <DimmedDuration
          className={cn(
            'text-sm opacity-50',
            isOverdue && 'text-red-600 opacity-100',
          )}
          ms={groupTiming.actual.duration}
          roundingFn={Math.round}
        />
      }
      Range={
        <GroupRange
          start={groupTiming.actual.start}
          end={groupTiming.actual.end}
          startClassName={cn(
            'opacity-50',
            isBefore(groupTiming.actual.start, groupTiming.original.start) &&
              'text-green-600 opacity-100',
            isAfter(groupTiming.actual.start, groupTiming.original.start) &&
              'text-red-600 opacity-100',
          )}
          endClassName={cn(
            'opacity-50',
            isBefore(groupTiming.actual.end, groupTiming.original.end) &&
              'text-green-600 opacity-100',
            isAfter(groupTiming.actual.end, groupTiming.original.end) &&
              'text-red-600 opacity-100',
          )}
          timezone={timezone}
          todDisplayFormat={todDisplayFormat}
        />
      }
    />
  )
}
